import {
  Dialog,
  DialogContent,
  Typography,
  Button,
  Stack,
  Box,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import { FaUsers, FaCoins, FaCrown } from "react-icons/fa";

const StudentLimitModal = ({ open, onClose, message, onUpgrade }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        component: motion.div,
        initial: { opacity: 0, scale: 0.9, y: 40 },
        animate: { opacity: 1, scale: 1, y: 0 },
        exit: { opacity: 0, scale: 0.9, y: 40 },
        transition: { duration: 0.4, ease: "easeOut" },
        sx: {
          borderRadius: 4,
          overflow: "hidden",
          boxShadow: "0px 8px 24px rgba(0,0,0,0.12)",
          backgroundColor: "#fff",
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Header */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #fdfbfb, #ebedee)",
            color: "#1e293b",
            p: isMobile ? 2.5 : 3,
            textAlign: "center",
          }}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            <Box
              sx={{
                width: 70,
                height: 70,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #fef3c7, #fde68a)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
                boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
              }}
            >
              <FaCrown size={28} color="#d97706" />
            </Box>
          </motion.div>

          <Typography
            sx={{
              fontWeight: 800,
              fontSize: "1.25rem",
              color: "#0f172a",
            }}
          >
            Student Limit Reached
          </Typography>

          <Typography
            sx={{
              mt: 1,
              color: "#475569",
              fontSize: "0.95rem",
              lineHeight: 1.6,
            }}
          >
            {message}
          </Typography>
        </Box>

        {/* Body */}
        <Box sx={{ p: isMobile ? 2.5 : 3 }}>
          <Typography
            sx={{
              color: "#334155",
              fontSize: "0.95rem",
              lineHeight: 1.7,
              mb: 2.5,
            }}
          >
            To continue adding more students, upgrade your subscription plan or
            purchase additional student coins.
          </Typography>

          {/* Features */}
          <Stack spacing={1.5} sx={{ mb: 3 }}>
            <motion.div
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <FaUsers color="#0284c7" />
                <Typography sx={{ fontSize: "0.9rem", color: "#1e293b" }}>
                  Add more students instantly
                </Typography>
              </Stack>
            </motion.div>

            <motion.div
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <FaCoins color="#f59e0b" />
                <Typography sx={{ fontSize: "0.9rem", color: "#1e293b" }}>
                  Flexible coin-based expansion
                </Typography>
              </Stack>
            </motion.div>

            <motion.div
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <FaCrown color="#7c3aed" />
                <Typography sx={{ fontSize: "0.9rem", color: "#1e293b" }}>
                  Unlock premium features
                </Typography>
              </Stack>
            </motion.div>
          </Stack>

          {/* Actions */}
          <Stack spacing={1.2}>
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={onUpgrade}
                sx={{
                  py: 1.3,
                  borderRadius: 2.5,
                  textTransform: "none",
                  fontWeight: 700,
                  background: "linear-gradient(135deg, #3b82f6, #0ea5e9)",
                  boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
                }}
              >
                Upgrade / Buy Coins
              </Button>
            </motion.div>

            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={onClose}
                sx={{
                  py: 1.2,
                  borderRadius: 2.5,
                  textTransform: "none",
                  fontWeight: 600,
                  borderColor: "#cbd5e1",
                  color: "#475569",
                  "&:hover": {
                    borderColor: "#94a3b8",
                    backgroundColor: "#f8fafc",
                  },
                }}
              >
                Maybe Later
              </Button>
            </motion.div>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default StudentLimitModal;
